const PARSER_KEYS = [
  'parser_type',
  'command_dialect',
  'hybrid_type',
  'hybrid_changes',
  'parser_path',
  'trace_id',
];

function inputFormat(params) {
  if (params.image) return params.text ? 'image_text' : 'image';
  if (params.pnr) return 'pnr';
  return params.fare_params ? 'form' : 'text';
}

function knownCriteria(params) {
  const { flights, passengers = {} } = params.fare_params || {};
  return {
    ...(flights?.length
      ? {
          route: flights
            .map((f) => `${f.departure_airport_code}-${f.arrival_airport_code}`)
            .join(';'),
          departure_dates: flights.map((f) => f.departure_date).join(';'),
          flights_count: flights.length,
        }
      : {}),
    ...Object.fromEntries(
      ['adults', 'children', 'infants']
        .filter((key) => passengers[key] != null)
        .map((key) => [key, passengers[key]])
    ),
  };
}

// Analytics must never interrupt a search or the rendering of its results.
export function emitSearchEvent(emit, name, properties) {
  try {
    emit(name, properties);
  } catch {
    /* SDK unavailable */
  }
}

export class SearchAnalytics {
  constructor({ params, source, spot, emit, searchId = crypto.randomUUID() }) {
    source ||= params.fare_params ? 'Form' : params.command ? 'Terminal' : 'Magic';
    this.context = {
      search_id: searchId,
      source,
      input_format: inputFormat(params),
      spot: spot || { Form: 'form', Terminal: 'terminal', Magic: 'copilot' }[source],
    };
    this.properties = { platform: 'Site', ...knownCriteria(params), ...this.context };
    this.emit = emit;
    this.finished = false;
    emitSearchEvent(emit, 'Search', { ...this.properties });
  }

  receive(metadata = {}) {
    if (!metadata || typeof metadata !== 'object') return;
    for (const key of PARSER_KEYS) {
      if (metadata[key] != null) this.properties[key] = metadata[key];
    }
  }

  finish(status, { event, errorMessage } = {}) {
    if (this.finished) return;
    this.finished = true;
    if (status === 'aborted' || status === 'parse_only') return;

    const name = status === 'error' ? 'Error' : 'Fare';
    const serverProperties = event?.event_type === name ? event.event_properties : {};
    const properties = { ...serverProperties, ...this.properties, status };
    if (name === 'Error') {
      properties.event_name = 'Fare';
      properties.error_message = serverProperties?.error_message || errorMessage || 'Search failed';
    }
    emitSearchEvent(this.emit, name, properties);
  }
}

// Preserve the production "Search results" schema and its twenty-option sample.
export function searchResultsProperties(destinations, result, sort) {
  const prices = destinations
    .map((d) => d.cheaper_price?.amount)
    .filter((amount) => typeof amount === 'number');
  return {
    search_id: result.search_id,
    search_result_id: result.search_result_id,
    type: result.request_type,
    input_type: result.request_source,
    channels: result.channels,
    sort,
    offers_count: destinations.length,
    cheapest_price: prices.length ? Math.min(...prices) : null,
    currency: destinations[0]?.cheaper_price?.currency,
    options: destinations.slice(0, 20).map((destination, index) => {
      const flights = destination.flights || [];
      const seconds = flights.reduce((sum, flight) => sum + (flight.seconds_journey_time || 0), 0);
      const stops = flights.reduce(
        (sum, flight) => sum + (flight.flight_type?.connection?.count || 0),
        0
      );
      return [
        index + 1,
        destination.channel,
        flights[0]?.segments?.[0]?.carrier?.airline_code,
        flights[0]?.departure_time,
        seconds ? Math.round(seconds / 60) : null,
        stops,
        destination.cheaper_price?.amount,
      ].join('|');
    }),
  };
}
